import React, { useEffect, useRef, useState } from 'react';
import DialogModal from '../../../components/DialogModal';
import {
  getDeclarationTemplateVariables,
  getDeclarationHtml,
  uploadDeclarationFile
} from '../../../apis/declarationSetup';
import {
  DeclarationFieldWrapper,
  FileWrapper,
  OptionsWrapper,
  StyledButton
} from '../styled';
import { DeclarationItem, FileType, SchoolDeclarationType } from '../types';
import { templateVariableMapping } from '../utils/templateVariableMapping';
import { Box, CircularProgress } from '@mui/material';
import TextInputWithIcon from '../../../components/TextInputWithIcon';
import { DataGrid } from '@mui/x-data-grid';
import { dataGridSx } from '../../common/styled/DataGrid';
import pallet from '../../../layouts/pallet';
import { declarationFileColumns } from '../utils/declarationFileColumns';
import ViewDeclarationFileDialog from './ViewDeclarationFileDialog';
import ConfirmationDialog from '../../../components/ConfirmationDialog/ConfirmationDialog';
import SelectAll from '../../../components/SelectAllField/SelectAllField';

interface DeclarationModalProps {
  open: boolean;
  onClose: () => void;
  mode: 'add' | 'edit';
  declaration?: DeclarationItem | null;
  onSave?: (item: DeclarationItem) => void;
  selectedDeclaration?: SchoolDeclarationType | null;
  setSelectedDeclaration?: React.Dispatch<
    React.SetStateAction<SchoolDeclarationType | null>
  >;
  onApiSuccessAndFailure: (isSuccess: boolean, message: string) => void;
}

const DeclarationModal: React.FC<DeclarationModalProps> = ({
  open,
  onClose,
  mode,
  declaration,
  onSave,
  selectedDeclaration,
  setSelectedDeclaration,
  onApiSuccessAndFailure
}) => {
  const [declarationName, setDeclarationName] = useState<string>('');
  const [sequenceNo, setSequenceNo] = useState<number>(0);
  const [uploading, setUploading] = useState<boolean>(false);
  const [files, setFiles] = useState<FileType[]>([]);
  const [selectedDynamicValues, setSelectedDynamicValues] = useState<string[]>(
    []
  );
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const [dynamicValueOptions, setDynamicValueOptions] = useState<any[]>([]);
  const [loadingDynamicOptions, setLoadingDynamicOptions] =
    useState<boolean>(false);

  const [isViewFileDialogOpen, setIsViewFileDialogOpen] =
    useState<boolean>(false);
  const [fileUrlToView, setFileUrlToView] = useState<string | null>(null);
  const [isProcessingFile, setIsProcessingFile] = useState<boolean>(false);
  const [declarationTitle, setDeclarationTitle] = useState<string>('');
  const [isDeclarationDeleteModalOpen, setIsDeclarationDeleteModalOpen] =
    useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [selectedDeclarationItem, setSelectedDeclarationItem] =
    useState<DeclarationItem | null>(null);

  useEffect(() => {
    const fetchOptions = async () => {
      setLoadingDynamicOptions(true);
      try {
        const data = await getDeclarationTemplateVariables();
        const items = Array.isArray(data) ? data : [];
        setDynamicValueOptions(items || []);
      } catch {
        setDynamicValueOptions([]);
      } finally {
        setLoadingDynamicOptions(false);
      }
    };
    fetchOptions();
  }, []);

  useEffect(() => {
    if (mode === 'edit' && declaration) {
      setDeclarationName(declaration.label || '');
      setSequenceNo(declaration.sequenceNo || 0);
      setFiles(
        declaration.textTemplateFileName
          ? [
              {
                id: `${Date.now()}`,
                name: declaration.textTemplateFileName.split('/').pop() || '',
                url: declaration.textTemplateFileName
              }
            ]
          : []
      );
      setSelectedDynamicValues(
        Array.isArray(declaration.templateVariables)
          ? declaration.templateVariables.map((v) => v.mappingName)
          : []
      );
    } else if (mode === 'add') {
      setDeclarationName('');
      setSequenceNo(0);
      setFiles([]);
      setSelectedDynamicValues([]);
    }
  }, [declaration, open, mode]);

  const handleModalClose = () => {
    setSequenceNo(0);
    setDeclarationName('');
    setFiles([]);
    setSelectedDynamicValues([]);
    onClose();
  };

  const handleDeleteDeclarationCancel = () => {
    setIsDeleting(false);
    setIsDeclarationDeleteModalOpen(false);
    setSelectedDeclarationItem(null);
  };

  const handleDeleteDeclaration = (fileItem: DeclarationItem) => {
    setSelectedDeclarationItem(fileItem);
    setIsDeclarationDeleteModalOpen(true);
  };

  const handleDeleteDeclarationConfirm = async () => {
    if (!selectedDeclarationItem?.id) {
      handleDeleteDeclarationCancel();
      return;
    }

    setFiles((prevFiles) =>
      prevFiles.filter((file) => file.id !== selectedDeclarationItem.id)
    );
    handleDeleteDeclarationCancel();
  };

  const handleFileLabelClick = async (file: FileType) => {
    if (
      !selectedDeclaration ||
      !selectedDeclaration.school ||
      !selectedDeclaration.academicYear
    ) {
      onApiSuccessAndFailure(
        false,
        'School/Academic Year information missing to view file.'
      );
      return;
    }

    setIsProcessingFile(true);
    setIsViewFileDialogOpen(true);
    setFileUrlToView(null);
    setDeclarationTitle(file.name);

    try {
      const fileNameOnly = file.url.split('/').at(-1) || '';
      const response = await getDeclarationHtml({
        school: selectedDeclaration.school,
        academicYear: selectedDeclaration.academicYear,
        templateFileName: fileNameOnly
      });
      setFileUrlToView(response.data);
    } catch (error) {
      onApiSuccessAndFailure(false, 'Failed to fetch declaration content');
      setIsViewFileDialogOpen(false);
    } finally {
      setIsProcessingFile(false);
    }
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      let url = '';
      let fileNameToDisplay = file.name;
      if (
        !selectedDeclaration ||
        !selectedDeclaration?.school ||
        !selectedDeclaration?.academicYear ||
        !selectedDeclaration?.grade
      ) {
        setUploading(false);
        return;
      }
      const response = await uploadDeclarationFile({
        school: selectedDeclaration.school,
        academicYear: selectedDeclaration.academicYear,
        grade: selectedDeclaration.grade,
        file
      });
      const fileUploadResponse = response as any;
      url = fileUploadResponse.fileName;
      fileNameToDisplay = url.split('/').pop() || file.name;

      const item = {
        id: `${Date.now()}`,
        name: fileNameToDisplay,
        url
      };
      setFiles([item]);
      onApiSuccessAndFailure(true, `File uploaded successfully`);
    } catch (error: any) {
      onApiSuccessAndFailure(
        false,
        error.errors?.[0].messages || `Error in uploading file`
      );
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleAction = async () => {
    const templateVariables = templateVariableMapping(
      selectedDynamicValues,
      dynamicValueOptions
    );

    if (mode === 'edit' && declaration && onSave) {
      const updatedItem: DeclarationItem = {
        ...declaration,
        label: declarationName,
        sequenceNo: Number(sequenceNo),
        textTemplateFileName: files[0]?.url.split('/').pop() || '',
        templateVariables: templateVariables as any
      };
      onSave(updatedItem);
      handleModalClose();
    } else if (mode === 'add' && setSelectedDeclaration) {
      const newDeclaration: DeclarationItem = {
        label: declarationName,
        sequenceNo: Number(sequenceNo),
        textTemplateFileName: files[0]?.url.split('/').pop() || '',
        templateVariables: templateVariables
      } as DeclarationItem;

      setSelectedDeclaration((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          declarations: [...(prev.declarations || []), newDeclaration]
        };
      });
      handleModalClose();
    }
  };

  const isButtonDisabled = uploading || !declarationName || files.length === 0;

  return (
    <>
      <DialogModal
        open={open}
        onClose={handleModalClose}
        title={mode === 'add' ? 'Add Declaration' : 'Edit Declaration'}
        maxWidth='md'
        actions={
          <StyledButton disabled={isButtonDisabled} onClick={handleAction}>
            {uploading && <CircularProgress size={18} />}
            {!uploading && (mode === 'add' ? 'Add' : 'Save')}
          </StyledButton>
        }
      >
        <DeclarationFieldWrapper>
          <TextInputWithIcon
            fullWidth
            label='Sequence No'
            placeholder='Enter Sequence No.'
            type='number'
            value={sequenceNo}
            inputProps={{ min: 0 }}
            onChange={(e) => {
              const value = Math.max(0, Number(e.target.value));
              setSequenceNo(value);
            }}
            hasTopLabel
          />
          <TextInputWithIcon
            fullWidth
            label='Declaration Name'
            placeholder='Enter Declaration Name'
            value={declarationName}
            onChange={(e) => setDeclarationName(e.target.value)}
            hasTopLabel
          />
          <FileWrapper>
            <input
              ref={fileInputRef}
              type='file'
              accept='.docx'
              hidden
              onChange={handleFileUpload}
            />
            <StyledButton
              disabled={uploading}
              onClick={() => {
                if (fileInputRef.current) {
                  fileInputRef.current.value = '';
                  fileInputRef.current.click();
                }
              }}
            >
              {uploading ? <CircularProgress size={18} /> : 'Add File'}
            </StyledButton>
          </FileWrapper>
          <DataGrid
            autoHeight
            rows={files}
            getRowId={(r) => r.id}
            columns={declarationFileColumns(
              handleFileLabelClick,
              handleDeleteDeclaration
            )}
            disableRowSelectionOnClick
            hideFooterSelectedRowCount
            pageSizeOptions={[5]}
            sx={{
              width: '100%',
              minHeight: 160,
              ...dataGridSx(pallet),
              '& .MuiDataGrid-virtualScroller': { overflowY: 'auto' }
            }}
          />
          {loadingDynamicOptions ? (
            <OptionsWrapper>
              <CircularProgress size={22} />
              <Box ml={2}>Loading dynamic values...</Box>
            </OptionsWrapper>
          ) : (
            <SelectAll
              label='Dynamic Values'
              placeholder='Select Dynamic Values'
              options={dynamicValueOptions.map((opt: any) => ({
                label: opt.displayName,
                value: opt.mappingName
              }))}
              selected={selectedDynamicValues}
              onChange={setSelectedDynamicValues}
              hasTopLabel
              formControlMargin='none'
            />
          )}
        </DeclarationFieldWrapper>
      </DialogModal>
      <ViewDeclarationFileDialog
        open={isViewFileDialogOpen}
        onClose={() => {
          setIsViewFileDialogOpen(false);
          setFileUrlToView(null);
        }}
        fileUrl={fileUrlToView}
        isLoading={isProcessingFile}
        title={declarationTitle}
      />
      <ConfirmationDialog
        open={isDeclarationDeleteModalOpen}
        onClose={handleDeleteDeclarationCancel}
        message={`Are you sure you want to delete the selected declaration?`}
        onConfirm={handleDeleteDeclarationConfirm}
        disable={isDeleting}
      />
    </>
  );
};

export default DeclarationModal;
